import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import merchantService from '../../services/merchantService';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MerchantProducts = () => {
  const { userProfile, session } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    product_name: '',
    description: '',
    original_price: '',
    category: '',
    stock_quantity: 0,
    is_available: true,
    image: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchMerchantProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      const token = userProfile?.token || session?.access_token;
      console.log('Token available:', !!token);
      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok && data.success) {
        setProducts(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [userProfile?.token, session?.access_token]);

  useEffect(() => {
    fetchMerchantProducts();
  }, [fetchMerchantProducts]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewProduct({...newProduct, image: file});
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const formData = new FormData();
      formData.append('product_name', newProduct.product_name);
      formData.append('description', newProduct.description || '');
      formData.append('original_price', parseFloat(newProduct.original_price));
      formData.append('category', newProduct.category || '');
      formData.append('stock_quantity', newProduct.stock_quantity);
      formData.append('is_available', newProduct.is_available);
      
      if (newProduct.image) {
        formData.append('image', newProduct.image);
      }

      console.log('Sending product data:', Object.fromEntries(formData));

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      console.log('Add product response status:', response.status);
      const data = await response.json();
      console.log('Add product response data:', data);

      if (response.ok && data.success) {
        setProducts([data.data, ...products]);
        setShowAddModal(false);
        setNewProduct({
          product_name: '',
          description: '',
          original_price: '',
          category: '',
          stock_quantity: 0,
          is_available: true,
          image: null
        });
        setImagePreview(null);
      } else {
        setError(data.error || 'Failed to add product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setError('Failed to add product');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleProductStatus = async (productId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const data = await merchantService.updateProduct(productId, { is_available: newStatus });

      if (data.success) {
        setProducts(products.map(product => 
          product.id === productId 
            ? { ...product, is_available: newStatus }
            : product
        ));
      }
    } catch (error) {
      console.error('Error updating product status:', error);
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setNewProduct({
      product_name: product.product_name,
      description: product.description || '',
      original_price: product.original_price,
      category: product.category || '',
      stock_quantity: product.stock_quantity,
      is_available: product.is_available,
      image: null
    });
    setImagePreview(product.image_url || null);
    setShowEditModal(true);
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const formData = new FormData();
      formData.append('product_name', newProduct.product_name);
      formData.append('description', newProduct.description || '');
      formData.append('original_price', parseFloat(newProduct.original_price));
      formData.append('category', newProduct.category || '');
      formData.append('stock_quantity', newProduct.stock_quantity);
      formData.append('is_available', newProduct.is_available);
      
      if (newProduct.image) {
        formData.append('image', newProduct.image);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProducts(products.map(product => 
          product.id === editingProduct.id ? data.data : product
        ));
        setShowEditModal(false);
        setEditingProduct(null);
        setImagePreview(null);
      } else {
        setError(data.error || 'Failed to update product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      setError('Failed to update product');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const data = await merchantService.deleteProduct(productId);
      if (data.success) {
        setProducts(products.filter(product => product.id !== productId));
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const deleteProductImage = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product image?')) return;
    
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/merchant/products/${productId}/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProducts(products.map(product => 
          product.id === productId ? { ...product, image_url: null } : product
        ));
        if (editingProduct && editingProduct.id === productId) {
          setImagePreview(null);
        }
      } else {
        setError(data.error || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting product image:', error);
      setError('Failed to delete image');
    }
  };

  const fixMerchantRegistration = async () => {
    try {
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      console.log('Fixing merchant registration...');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/fix-merchant-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Fix merchant response status:', response.status);
      const data = await response.json();
      console.log('Fix merchant response data:', data);

      if (response.ok && data.success) {
        setError(null);
        // Retry fetching products
        fetchMerchantProducts();
      } else {
        setError(data.error || 'Failed to fix merchant registration');
      }
    } catch (error) {
      console.error('Error fixing merchant registration:', error);
      setError('Failed to fix merchant registration');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setImagePreview(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header shimmer */}
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 w-64 bg-muted rounded mb-2 animate-pulse"></div>
            <div className="h-4 w-96 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="h-12 w-32 bg-muted rounded animate-pulse"></div>
        </div>
        
        {/* Products grid shimmer */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="h-6 w-32 bg-muted rounded animate-pulse"></div>
                      <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                    </div>
                    <div className="h-6 w-16 bg-muted rounded animate-pulse"></div>
                  </div>
                  <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-muted rounded animate-pulse"></div>
                  <div className="flex justify-between items-center">
                    <div className="h-8 w-24 bg-muted rounded animate-pulse"></div>
                    <div className="h-6 w-20 bg-muted rounded animate-pulse"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Products 📦"
        description="Manage your product catalog"
        actions={
          <Button onClick={() => setShowAddModal(true)}>+ Add Product</Button>
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center justify-between pt-6 text-destructive">
            <span>{error}</span>
            {error.includes('Merchant not found') && (
              <Button variant="destructive" size="sm" onClick={fixMerchantRegistration}>
                Fix Registration
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {products.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No products yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Start by adding your first product to your catalog
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(product => (
            <Card key={product.id} className="overflow-hidden">
              {product.image_url && (
                <div className="h-48 bg-muted">
                  <img 
                    src={product.image_url} 
                    alt={product.product_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <CardTitle className="text-lg">{product.product_name}</CardTitle>
                    <CardDescription>{product.category || 'No category'}</CardDescription>
                  </div>
                  <Badge variant={product.is_available ? 'success' : 'destructive'}>
                    {product.is_available ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  {product.description || 'No description'}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-foreground">
                    SAR {product.original_price}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    Stock: {product.stock_quantity}
                  </span>
                </div>
              </CardContent>
              <CardFooter className="gap-2">
                <Button className="flex-1" size="sm" onClick={() => openEditModal(product)}>
                  Edit
                </Button>
                <Button
                  className="flex-1"
                  size="sm"
                  variant={product.is_available ? 'destructive' : 'default'}
                  onClick={() => toggleProductStatus(product.id, product.is_available)}
                >
                  {product.is_available ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => deleteProduct(product.id)}>
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddProduct} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product_name">Product Name *</Label>
              <Input
                id="product_name"
                type="text"
                value={newProduct.product_name}
                onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="original_price">Price (SAR) *</Label>
              <Input
                id="original_price"
                type="number"
                step="0.01"
                min="0"
                value={newProduct.original_price}
                onChange={(e) => setNewProduct({...newProduct, original_price: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                type="text"
                value={newProduct.category}
                onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stock_quantity">Stock Quantity</Label>
              <Input
                id="stock_quantity"
                type="number"
                min="0"
                value={newProduct.stock_quantity}
                onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product_image">Product Image</Label>
              <Input
                id="product_image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover rounded-md border"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_available"
                checked={newProduct.is_available}
                onCheckedChange={(v) => setNewProduct({...newProduct, is_available: v})}
              />
              <Label htmlFor="is_available">Available for sale</Label>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Adding...' : 'Add Product'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={(open) => { if (!open) closeEditModal(); else setShowEditModal(true); }}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProduct} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_product_name">Product Name *</Label>
              <Input
                id="edit_product_name"
                type="text"
                value={newProduct.product_name}
                onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_original_price">Price (SAR) *</Label>
              <Input
                id="edit_original_price"
                type="number"
                step="0.01"
                min="0"
                value={newProduct.original_price}
                onChange={(e) => setNewProduct({...newProduct, original_price: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_category">Category</Label>
              <Input
                id="edit_category"
                type="text"
                value={newProduct.category}
                onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_stock_quantity">Stock Quantity</Label>
              <Input
                id="edit_stock_quantity"
                type="number"
                min="0"
                value={newProduct.stock_quantity}
                onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_product_image">Product Image</Label>
              <Input
                id="edit_product_image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2 flex items-center gap-2">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover rounded-md border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setNewProduct({...newProduct, image: null});
                      setImagePreview(null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}
              {editingProduct && editingProduct.image_url && !imagePreview && (
                <div className="mt-2 flex items-center gap-2">
                  <img 
                    src={editingProduct.image_url} 
                    alt="Current" 
                    className="w-20 h-20 object-cover rounded-md border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteProductImage(editingProduct.id)}
                  >
                    Delete Current
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="edit_is_available"
                checked={newProduct.is_available}
                onCheckedChange={(v) => setNewProduct({...newProduct, is_available: v})}
              />
              <Label htmlFor="edit_is_available">Available for sale</Label>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeEditModal} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? 'Updating...' : 'Update Product'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantProducts;
